import { Component, Prop, h, Build, Host, Element } from '@stencil/core';
import { ACTION_REGISTRY } from '../../../../../actions/_generated-registry';

const COMPONENT_NAME = 'sn-hero-header-type-9';
const CTA_ACTION = 'ui:sn-open-editor' as const;

/**
 * Hero header for SignNow forms pages with a background document preview image and a crawlable "Get Form" link.
 * Centered heading, description and CTA over a dimmed preview image that adapts to viewport size (default, large).
 * Unlike `sn-hero-header-type-2` it has no disclaimer panel, the heading is a plain heading, and the CTA is an `<a href>` present in the server-rendered markup.
 *
 * @product signnow
 * @source intro
 * @version 1.0.0
 * @category SignNow
 *
 * @slot heading - {required}, <me-heading variant="1" tag="div"> - Main hero heading text (the component already wraps this slot in the page <h1>, so the slotted me-heading must render a non-heading `div` to avoid a nested/duplicate h1)
 * @slot description - <me-text tag="p"> - Section description text below the heading
 *
 * @example
 *   <sn-hero-header-type-9
 *     form-id="2"
 *     button-text="Get Form"
 *     button-href="https://www.signnow.com/"
 *     image-default="https://www.signnow.com/preview/454/879/454879723/large.png"
 *     image-large="https://www.signnow.com/preview/454/879/454879723/large.png"
 *   >
 *     <me-heading slot="heading" variant="1" tag="div">Lease Agreement Form</me-heading>
 *     <me-text slot="description" tag="p">Create a custom Lease Agreement Form that meets your industry specifications.</me-text>
 *   </sn-hero-header-type-9>
 */
@Component({
  tag: 'sn-hero-header-type-9',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnHeroHeaderType9 {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Unique form identifier. Stamped as `data-formid` on the section and handed to the editor
   * action when no `button-href` is set
   */
  @Prop({ attribute: 'form-id', reflect: true }) formId!: string;

  /**
   * CTA button label text
   * @content
   * @default Get Form
   */
  @Prop({ attribute: 'button-text' }) buttonText: string = 'Get Form';

  /**
   * Editor URL the CTA links to. When set the CTA is a plain `<a href>`; when empty it falls
   * back to the `ui:sn-open-editor` action
   */
  @Prop({ attribute: 'button-href' }) buttonHref: string = '';

  /**
   * Background image URL for default / mobile viewports
   */
  @Prop({ attribute: 'image-default' }) imageDefault: string = '';

  /**
   * Background image URL for large viewports (960px+)
   */
  @Prop({ attribute: 'image-large' }) imageLarge: string = '';

  @Element() host!: HTMLElement;

  componentDidLoad() {
    if (!Build.isBrowser) return;

    // Only the fallback needs the action module; a link navigates without it. Warmed while idle for
    // the same reason as in sn-hero-header-type-2: fetched at click time it costs ~500ms on slow devices.
    if (!this.buttonHref) {
      const warm = () => { void ACTION_REGISTRY[CTA_ACTION]?.(); };
      if ('requestIdleCallback' in window) {
        (window as unknown as { requestIdleCallback: (cb: () => void, o?: object) => void })
          .requestIdleCallback(warm, { timeout: 500 });
      } else {
        setTimeout(warm, 100);
      }
    }

    // Readiness contract for automation, kept identical to sn-hero-header-type-2. See MEP-1367.
    this.host.setAttribute('data-cta-ready', '');
  }

  private renderCta() {
    if (this.buttonHref) {
      return (
        <me-button variant="primary" tag="a" href={this.buttonHref} class={`${COMPONENT_NAME}__cta`}>
          {this.buttonText}
        </me-button>
      );
    }

    // Fallback until the page payload carries the editor URL. `triggerActions` stays a string so it
    // reflects into the SSR markup; see the note in sn-hero-header-type-2.
    return (
      <me-button
        variant="primary"
        tag="button"
        class={`${COMPONENT_NAME}__cta js-editor-link`}
        triggerActions={CTA_ACTION}
        actionData={{ [CTA_ACTION]: { formId: this.formId } }}
      >
        {this.buttonText}
      </me-button>
    );
  }

  render() {
    return (
      <Host data-color-mode={this.colorMode}>
        <section class={COMPONENT_NAME} data-formid={this.formId}>
          {this.imageDefault && (
            <picture class={`${COMPONENT_NAME}__background`}>
              {this.imageLarge && <source media="(min-width: 960px)" srcSet={this.imageLarge} />}
              {/* LCP element on a forms page; `fetchpriority="high"` for the reason given in sn-hero-header-type-2. */}
              <img
                src={this.imageDefault}
                alt=""
                loading="eager"
                decoding="async"
                class={`${COMPONENT_NAME}__background-img`}
                {...({ fetchpriority: 'high' } as any)}
              />
            </picture>
          )}

          <div class={`${COMPONENT_NAME}__content`}>
            <h1 class={`${COMPONENT_NAME}__heading`}>
              <slot name="heading"></slot>
            </h1>

            <div class={`${COMPONENT_NAME}__description`}>
              <slot name="description"></slot>
            </div>

            {this.renderCta()}
          </div>
        </section>
      </Host>
    );
  }
}
